import { type ForwardRefExoticComponent, Fragment, forwardRef } from 'react';

import { Slottable } from '../slot';
import { ButtonIcon } from './button-icon';
import { ButtonLabel } from './button-label';
import { ButtonLoading } from './button-loading';
import { ButtonRoot } from './button-root';
import type { ButtonPropsInterface } from './types/button.types';

/**
 * @since 1.0.5
 */
export const Button = forwardRef<HTMLButtonElement, ButtonPropsInterface>(
  (props: ButtonPropsInterface, forwardedRef) => {
    const {
      asChild = false,
      children,
      icon,
      iconPosition = 'start',
      loading = false,
      ...rest
    } = props;

    return (
      <Button.Root ref={forwardedRef} asChild={asChild} loading={loading} {...rest}>
        <Slottable child={children}>
          {(slottable) => (
            <Fragment>
              {icon && iconPosition === 'start' ? <Button.Icon>{icon}</Button.Icon> : null}
              <Button.Label>{slottable}</Button.Label>
              {icon && iconPosition === 'end' ? <Button.Icon>{icon}</Button.Icon> : null}
              {loading ? <Button.Loading /> : null}
            </Fragment>
          )}
        </Slottable>
      </Button.Root>
    );
  },
) as ForwardRefExoticComponent<ButtonPropsInterface> & {
  Root: typeof ButtonRoot;
  Label: typeof ButtonLabel;
  Icon: typeof ButtonIcon;
  Loading: typeof ButtonLoading;
};

Button.displayName = 'Button';

Button.Root = ButtonRoot;
Button.Label = ButtonLabel;
Button.Icon = ButtonIcon;
Button.Loading = ButtonLoading;
